import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { data, redirect, useFetchers } from 'react-router'
import { z } from 'zod'
import { setConsent } from '#app/utils/consent.server.ts'
import { type ConsentDecision, type ConsentState } from '#app/utils/consent.ts'
import { type Route } from './+types/consent.ts'

/**
 * The visitor's action on the consent banner: gated shows accept/decline;
 * cookieless shows a single `dismiss` ("Got it"); the settings-variant toggle
 * reuses accept/decline (EPT-144).
 */
const ConsentFormSchema = z.object({
	intent: z.enum(['accept', 'decline', 'dismiss']),
	// Only ever sent by the no-JS `<ServerOnly>` `<Form>`; JS fetchers must not
	// send it (a redirect from a single-fetch POST 404s through `routes/$`).
	redirectTo: z.string().optional(),
})

type ConsentIntent = z.infer<typeof ConsentFormSchema>['intent']

/** The recorded decision each banner intent persists. */
const decisionForIntent: Record<ConsentIntent, ConsentDecision> = {
	accept: 'accepted',
	decline: 'declined',
	dismiss: 'dismissed',
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, { schema: ConsentFormSchema })
	invariantResponse(submission.status === 'success', 'Invalid consent received')

	const { intent, redirectTo } = submission.value
	const responseInit = {
		headers: {
			'set-cookie': setConsent(decisionForIntent[intent], Date.now()),
		},
	}
	if (redirectTo) {
		return redirect(redirectTo, responseInit)
	}
	return data({ result: submission.reply() }, responseInit)
}

/**
 * The consent state an in-flight banner submission is switching to, if any, so
 * the banner can dismiss itself and `<Matomo/>` can boot/stop before the network
 * settles (mirrors `useOptimisticAccent`/`useOptimisticThemeMode`).
 */
export function useOptimisticConsentState(): ConsentState | undefined {
	const fetchers = useFetchers()
	const consentFetcher = fetchers.find(
		(f) => f.formAction === '/resources/consent',
	)
	if (!consentFetcher?.formData) return undefined
	const submission = parseWithZod(consentFetcher.formData, {
		schema: ConsentFormSchema,
	})
	if (submission.status !== 'success') return undefined
	return decisionForIntent[submission.value.intent]
}
